import { useState, useEffect } from 'react';

interface DropboxUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUploadComplete?: () => void;
}

export default function DropboxUploadModal({ isOpen, onClose, onUploadComplete }: DropboxUploadModalProps) {
  const [hasUploaded, setHasUploaded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setHasUploaded(false);
      setShowSuccess(false);
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleOpenDropbox = () => {
    // Open Dropbox file request page
    window.open('https://www.dropbox.com/request/nYZDfioXmIKrgXVWPALh', '_blank', 'noopener,noreferrer');
    setHasUploaded(true);
  };

  const handleSubmit = async () => {
    if (!hasUploaded) return;
    
    setIsSubmitting(true);
    
    // Simulate upload processing
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setIsSubmitting(false);
    setShowSuccess(true);
    
    // Call the callback after success
    if (onUploadComplete) {
      onUploadComplete();
    }
    
    // Auto close after showing success
    setTimeout(() => {
      onClose();
      setShowSuccess(false);
      setHasUploaded(false);
    }, 2000);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative w-full max-w-lg mx-4 bg-white rounded-3xl shadow-2xl overflow-hidden animate-modal-in">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700 transition-all duration-200 cursor-pointer z-10"
        >
          <i className="ri-close-line text-xl"></i>
        </button>

        {/* Success State */}
        {showSuccess ? (
          <div className="p-10 text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-[#2ECC71]/20 to-[#27AE60]/20 flex items-center justify-center animate-bounce-in">
              <i className="ri-check-line text-4xl text-[#2ECC71]"></i>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Upload Complete!</h2>
            <p className="text-sm text-gray-500">Your files have been received and are being processed.</p>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="bg-gradient-to-br from-[#FF6B6B]/5 to-[#FF8E8E]/10 p-8 pb-6 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-[#FF6B6B] to-[#FF8E8E] flex items-center justify-center shadow-lg shadow-[#FF6B6B]/25">
                <i className="ri-dropbox-fill text-3xl text-white"></i>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Upload via Dropbox</h2>
              <p className="text-sm text-gray-500 max-w-sm mx-auto">
                Upload your photos through Dropbox for virtual staging
              </p>
            </div>

            {/* Content */}
            <div className="p-8 pt-6">
              {/* Steps */}
              <div className="space-y-3 mb-8">
                <div className="flex items-center gap-4 p-4 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors">
                  <div className="w-8 h-8 rounded-full bg-[#FF6B6B]/10 text-[#FF6B6B] flex items-center justify-center font-bold text-sm flex-shrink-0">
                    1
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">Open Dropbox Upload</p>
                    <p className="text-xs text-gray-500">Click the button below to open Dropbox</p>
                  </div>
                  <div className="w-8 h-8 flex items-center justify-center">
                    <i className="ri-external-link-line text-gray-300"></i>
                  </div>
                </div>

                <div className="flex items-center gap-4 p-4 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors">
                  <div className="w-8 h-8 rounded-full bg-[#FF6B6B]/10 text-[#FF6B6B] flex items-center justify-center font-bold text-sm flex-shrink-0">
                    2
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">Add Your Photos</p>
                    <p className="text-xs text-gray-500">Drag & drop or select files (JPG, PNG, TIFF)</p>
                  </div>
                  <div className="w-8 h-8 flex items-center justify-center">
                    <i className="ri-image-add-line text-gray-300"></i>
                  </div>
                </div>

                <div className="flex items-center gap-4 p-4 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors">
                  <div className="w-8 h-8 rounded-full bg-[#FF6B6B]/10 text-[#FF6B6B] flex items-center justify-center font-bold text-sm flex-shrink-0">
                    3
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">Confirm & Submit</p>
                    <p className="text-xs text-gray-500">Return here and click submit to complete</p>
                  </div>
                  <div className="w-8 h-8 flex items-center justify-center">
                    <i className="ri-checkbox-circle-line text-gray-300"></i>
                  </div>
                </div>
              </div>

              {/* Buttons */}
              <div className="space-y-3">
                <button
                  onClick={handleOpenDropbox}
                  className={`w-full flex items-center justify-center gap-2.5 px-6 py-4 rounded-xl text-sm font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap ${
                    hasUploaded
                      ? 'bg-gray-100 text-gray-500'
                      : 'bg-gradient-to-r from-[#FF6B6B] to-[#FF8E8E] text-white hover:shadow-lg hover:shadow-[#FF6B6B]/25 hover:scale-[1.02]'
                  }`}
                >
                  <i className="ri-dropbox-fill text-lg"></i>
                  {hasUploaded ? 'Dropbox Opened' : 'Open Dropbox Upload Page'}
                  {!hasUploaded && <i className="ri-external-link-line text-sm opacity-70"></i>}
                  {hasUploaded && <i className="ri-check-line text-sm"></i>}
                </button>

                <button
                  onClick={handleSubmit}
                  disabled={!hasUploaded || isSubmitting}
                  className={`w-full flex items-center justify-center gap-2 px-6 py-4 rounded-xl text-sm font-semibold transition-all duration-300 whitespace-nowrap ${
                    hasUploaded && !isSubmitting
                      ? 'bg-gray-900 text-white hover:bg-gray-800 hover:scale-[1.02] cursor-pointer'
                      : 'bg-gray-100 text-gray-300 cursor-not-allowed'
                  }`}
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line text-lg animate-spin"></i>
                      Processing...
                    </>
                  ) : (
                    <>
                      Submit Upload
                      <i className="ri-arrow-right-line text-sm"></i>
                    </>
                  )}
                </button>
              </div>

              {/* Helper Text */}
              {!hasUploaded && (
                <p className="text-center text-xs text-gray-400 mt-4">
                  <i className="ri-information-line mr-1"></i>
                  Open Dropbox first to enable the submit button
                </p>
              )}

              {/* Supported Formats */}
              <div className="mt-6 pt-6 border-t border-gray-100">
                <div className="flex items-center justify-center gap-6 text-xs text-gray-400">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-file-image-line"></i>
                    JPG, PNG, TIFF
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-upload-cloud-line"></i>
                    Max 50MB
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-stack-line"></i>
                    Up to 100 files
                  </span>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      <style>{`
        @keyframes modal-in {
          from {
            opacity: 0;
            transform: scale(0.95) translateY(10px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
        .animate-modal-in {
          animation: modal-in 0.3s ease-out;
        }
        @keyframes bounce-in {
          0% {
            opacity: 0;
            transform: scale(0.3);
          }
          50% {
            transform: scale(1.05);
          }
          70% {
            transform: scale(0.9);
          }
          100% {
            opacity: 1;
            transform: scale(1);
          }
        }
        .animate-bounce-in {
          animation: bounce-in 0.5s ease-out;
        }
      `}</style>
    </div>
  );
}

// Utility function to open Dropbox upload modal from anywhere
export const openDropboxUpload = () => {
  const event = new CustomEvent('openDropboxUploadModal');
  window.dispatchEvent(event);
};
